import type {
  AthleteProfile,
  GrowthMilestone,
  MediaItem,
  SiteContent,
  SocialLinks
} from "./types";

function asObject(value: unknown, label: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null) {
    throw new Error(`Invalid ${label}: expected object`);
  }
  return value as Record<string, unknown>;
}

function reqString(obj: Record<string, unknown>, key: string, label: string): string {
  const value = obj[key];
  if (typeof value !== "string" || value.trim() === "") {
    throw new Error(`Invalid ${label}.${key}: expected non-empty string`);
  }
  return value;
}

function parseProfile(raw: unknown): AthleteProfile {
  const profile = asObject(raw, "profile");
  const heroVideoSrc = profile.heroVideoSrc;
  if (heroVideoSrc !== null && typeof heroVideoSrc !== "string") {
    throw new Error("Invalid profile.heroVideoSrc: expected string or null");
  }
  return {
    name: reqString(profile, "name", "profile"),
    nameEn: reqString(profile, "nameEn", "profile"),
    tagline: reqString(profile, "tagline", "profile"),
    heroVideoSrc,
    heroPoster: reqString(profile, "heroPoster", "profile")
  };
}

function parseGrowth(raw: unknown): GrowthMilestone[] {
  if (!Array.isArray(raw)) {
    throw new Error("Invalid growth: expected array");
  }
  return raw.map((item, index) => {
    const milestone = asObject(item, `growth[${index}]`);
    return {
      image: reqString(milestone, "image", `growth[${index}]`),
      caption: reqString(milestone, "caption", `growth[${index}]`),
      date: reqString(milestone, "date", `growth[${index}]`),
      place: reqString(milestone, "place", `growth[${index}]`)
    };
  });
}

function parseStream(raw: unknown): MediaItem[] {
  if (!Array.isArray(raw)) {
    throw new Error("Invalid stream: expected array");
  }
  return raw.map((item, index) => {
    const mediaItem = asObject(item, `stream[${index}]`);
    const type = mediaItem.type;
    if (type !== "image" && type !== "video") {
      throw new Error(`Invalid stream[${index}].type: expected 'image' | 'video'`);
    }
    const poster = mediaItem.poster;
    if (poster !== null && typeof poster !== "string") {
      throw new Error(`Invalid stream[${index}].poster: expected string or null`);
    }
    if (type === "video" && (poster === null || poster === "")) {
      throw new Error(`Invalid stream[${index}].poster: video items require a poster`);
    }
    return {
      type,
      src: reqString(mediaItem, "src", `stream[${index}]`),
      poster,
      caption: reqString(mediaItem, "caption", `stream[${index}]`)
    };
  });
}

function parseSocials(raw: unknown): SocialLinks {
  const socials = asObject(raw, "socials");
  return {
    xiaohongshu: reqString(socials, "xiaohongshu", "socials"),
    douyin: reqString(socials, "douyin", "socials"),
    instagram: reqString(socials, "instagram", "socials"),
    wechat: reqString(socials, "wechat", "socials"),
    email: reqString(socials, "email", "socials")
  };
}

export function validateSiteContent(raw: unknown): SiteContent {
  const content = asObject(raw, "content");
  return {
    profile: parseProfile(content.profile),
    growth: parseGrowth(content.growth),
    stream: parseStream(content.stream),
    socials: parseSocials(content.socials)
  };
}
